import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ValueCounter from "@/components/ValueCounter";
import PartnersGrid from "@/components/PartnersGrid";
import PartnerOfMonth from "@/components/PartnerOfMonth";
import PhoneMockup from "@/components/PhoneMockup";
import ReferralCallout from "@/components/ReferralCallout";
import { useT } from "@/context/LangContext";
import { ArrowRight, ShieldCheck, MapPin, BadgeCheck, Smartphone, CreditCard, QrCode, Handshake } from "lucide-react";

const APP_SCREEN = "/app-screen-v2.png";

export default function LandingPage() {
  const { t } = useT();
  const [partners, setPartners] = useState([]);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    (async () => {
      const [{ data: ps }, { data: s }] = await Promise.all([
        api.get("/partners"),
        api.get("/settings/public"),
      ]);
      setPartners(ps);
      setSettings(s);
    })();
  }, []);

  const maxSavings = Math.min(
    partners.filter((p) => p.counts_in_counter !== false).reduce((sum, p) => {
      return sum + (p.offer_value || 0);
    }, 0),
    settings?.global_monthly_cap ?? 40
  );

  return (
    <div className="App">
      <Header />

      {/* HERO */}
      <section 
        className="relative bg-white" 
        style={{ 
          paddingTop: '6rem',
          paddingBottom: '3rem',
          isolation: 'isolate',
          contain: 'layout',
          zIndex: 1,
        }}
        data-testid="hero-section"
      >
        <div className="container-app">
          <div style={{ maxWidth: '100%', overflow: 'visible' }}>
            <h1
              style={{
                fontSize: '28px',
                lineHeight: '1.2',
                fontWeight: 700,
                color: '#0b1220',
                fontFamily: '-apple-system, BlinkMacSystemFont, Helvetica, Arial, sans-serif',
                margin: 0,
                padding: 0,
                textShadow: 'none',
                background: 'transparent',
                position: 'relative',
                zIndex: 2,
                wordBreak: 'normal',
                overflowWrap: 'break-word',
              }}
              data-testid="hero-title"
            >
              <div style={{ display: 'block', color: '#0b1220' }}>
                {t("hero.title.before")}{t("hero.title.brand")}
              </div>
              <div style={{ display: 'block', color: '#65a30d', marginTop: '4px' }}>
                {t("hero.title.highlight")}
              </div>
            </h1>
            <p className="mt-5 text-base md:text-xl text-muted-foreground max-w-2xl leading-relaxed" data-testid="hero-subtitle">
              {t("hero.subtitle")}
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <a href="#partenaires" className="btn-navy" data-testid="hero-cta-partners">
                {t("hero.cta_partners")} <ArrowRight className="w-4 h-4" />
              </a>
              <a href="#counter" className="btn-ghost" data-testid="hero-cta-counter">
                {t("hero.cta_counter")}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* VALUE COUNTER */}
      <ValueCounter partners={partners} settings={settings} />

      {/* REFERRAL — Network advantage for non-members coming via a partner */}
      <ReferralCallout variant="b2c" />

      {/* MOBILE APP + MEMBER CARD SECTION */}
      <section className="section-pad bg-muted relative overflow-hidden" data-testid="mobile-app-section">
        <div className="absolute -top-20 -left-20 w-80 h-80 bg-lime-500 rounded-full blur-3xl opacity-20" />
        <div className="container-app relative">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <div className="inline-block chip bg-lime-500/20 text-navy-900 mb-4">{t("app.badge")}</div>
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-navy-900 leading-tight">
                {t("app.title.before")}<span className="text-lime-600">{t("app.title.highlight")}</span>{t("app.title.after")}
              </h2>
              <p className="mt-5 text-lg text-muted-foreground max-w-lg leading-relaxed">
                {t("app.subtitle")}
              </p>

              <div className="mt-8 space-y-4">
                <Feature icon={Smartphone} title={t("app.feature1.title")} desc={t("app.feature1.desc")} />
                <Feature icon={CreditCard} title={t("app.feature2.title")} desc={t("app.feature2.desc")} />
                <Feature icon={QrCode} title={t("app.feature3.title")} desc={t("app.feature3.desc")} />
              </div>

              <div className="mt-8">
                <a href="https://tgapp-link.mywellness.com/673440451" target="_blank" rel="noreferrer" className="btn-lime" data-testid="app-download-cta">
                  {t("app.cta")} <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>

            <div className="lg:col-span-6 order-1 lg:order-2 flex justify-center">
              <div className="relative">
                <div className="absolute -inset-8 bg-gradient-to-br from-lime-500/30 to-navy-900/5 rounded-[4rem] blur-2xl" />
                <PhoneMockup image={APP_SCREEN} className="relative" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PARTNER OF THE MONTH */}
      <PartnerOfMonth />

      {/* PARTNERS */}
      <section className="section-pad" id="partenaires" data-testid="partners-section">
        <div className="container-app">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div className="max-w-2xl">
              <div className="inline-block chip bg-lime-500/15 text-navy-900 mb-3">{t("partners.badge")}</div>
              <h2 className="font-heading font-extrabold text-4xl md:text-5xl text-navy-900 tracking-tight">
                {t("partners.title.before", { n: partners.length })}<br/>
                <span className="text-navy-900/60">{t("partners.title.after")}</span>
              </h2>
            </div>
          </div>
          <PartnersGrid showFilters={false} />
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="py-12 bg-navy-900 text-white" data-testid="trust-strip">
        <div className="container-app grid md:grid-cols-3 gap-8">
          <TrustItem icon={BadgeCheck} title={t("trust.verified.title")} desc={t("trust.verified.desc")} />
          <TrustItem icon={MapPin} title={t("trust.local.title")} desc={t("trust.local.desc")} />
          <TrustItem icon={ShieldCheck} title={t("trust.included.title")} desc={t("trust.included.desc")} />
        </div>
      </section>

      {/* PARTNER CTA — new copy */}
      <section className="section-pad bg-white" data-testid="partner-cta-section">
        <div className="container-app">
          <div className="rounded-3xl bg-lime-500 p-10 md:p-16 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-80 h-80 bg-lime-300 rounded-full blur-3xl opacity-60" />
            <div className="absolute top-8 right-8 md:top-12 md:right-12 hidden sm:block">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-navy-900 flex items-center justify-center shadow-lg rotate-[-6deg]">
                <Handshake className="w-7 h-7 md:w-8 md:h-8 text-lime-400" />
              </div>
            </div>
            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-block chip bg-navy-900 text-lime-500 mb-4">{t("cta.badge")}</div>
                <h2 className="font-heading font-extrabold text-3xl sm:text-4xl md:text-5xl text-navy-900 leading-tight">
                  {t("cta.title")}
                </h2>
                <p className="mt-5 text-navy-900/80 text-lg max-w-xl leading-relaxed">
                  {t("cta.body")}
                </p>
              </div>
              <div className="flex md:justify-end">
                <Link to="/devenir-partenaire" className="btn-navy text-base px-8 py-4" data-testid="cta-propose-offer">
                  {t("cta.button")} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function TrustItem({ icon: Icon, title, desc }) {
  return (
    <div className="flex gap-4">
      <div className="w-11 h-11 rounded-xl bg-lime-500/15 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-lime-400" />
      </div>
      <div>
        <div className="font-heading font-bold text-lg">{title}</div>
        <div className="text-sm text-white/60 mt-1">{desc}</div>
      </div>
    </div>
  );
}

function Feature({ icon: Icon, title, desc }) {
  return (
    <div className="flex gap-4">
      <div className="w-11 h-11 rounded-xl bg-navy-900 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-lime-400" />
      </div>
      <div>
        <div className="font-heading font-bold text-navy-900 text-lg">{title}</div>
        <div className="text-sm text-muted-foreground mt-0.5">{desc}</div>
      </div>
    </div>
  );
}
